import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { schriftgroesseAnwenden } from './lib/darstellung'
import { glasbandAnwenden } from './lib/glasband'
// Hört ab dem Start auf das Angebot des Browsers, die App zu installieren
// (#101): es kommt früh, oft vor dem ersten Zeichnen.
import './lib/installieren'
import { BrowserRouter } from 'react-router-dom'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { App } from './App'
import { Absturz } from './components/Absturz'
import { ApiError, fehlertext } from './api/client'
import { huelleAnmelden } from './lib/huelle'
import { tastaturBeobachten } from './lib/tastatur'
import { beobachteTippziel } from './lib/tippziel'
import { vorratLaden, vorratMitschreiben } from './lib/vorrat'
import { absageWeg, merkeAbsage } from './lib/absagen'
import { isAbort } from './lib/passkey'
import { notiere } from './lib/sendeprotokoll'
import { dropAnswersFromBeforeInvalidation } from './lib/ungueltig'
import './styles/app.css'

// Muss vor dem ersten Zeichnen stehen: die Höhe der Anwendung hängt daran.
tastaturBeobachten()

// **Und das hier ebenfalls vor dem ersten Zeichnen.** Beim Kaltstart aus einer
// Benachrichtigung heraus kommt das Ziel, lange bevor React steht; wer erst
// später zuhört, hat es verpasst und landet auf der Startseite.
beobachteTippziel()

/**
 * **Das Fangnetz für stille Absagen** (Codereview E2).
 *
 * Rund fünfzehn Stellen tippen etwas an und prüfen ihren Fehler nicht: einen
 * Punkt löschen, freigeben, verbergen, die Sammelfreigabe, die Regler der
 * Karte, die Routenoptionen, eine Aufzeichnung entfernen, ein Entwurf
 * verschieben, die Schalter der Reise, das Abmelden. Ohne Netz geht der Finger
 * runter und die Anwendung schweigt.
 *
 * Statt an jeder dieser Stellen dieselbe Zeile zu schreiben — die dann an der
 * sechzehnten wieder fehlt — steht sie hier einmal. Wer es besser kann, sagt
 * es über `meta.eigeneMeldung` ab: eine Absage, die dort steht, wo der Finger
 * war, ist die bessere, und zwei übereinander sind eine zu viel.
 *
 * Ein Abbruch ist keine Absage: ein weggewischtes Passkey-Blatt, eine
 * abgebrochene Aufnahme. Dafür gibt es `isAbort()`.
 */
const mutationCache = new MutationCache({
  onError: (fehler, _variablen, _context, mutation) => {
    if (mutation.meta?.eigeneMeldung === true || isAbort(fehler)) return
    merkeAbsage(fehlertext(fehler), fehler instanceof ApiError && fehler.status === 0)
  },
  // Ein Erfolg raeumt die alte Meldung weg: sie hat sich erledigt, und ein
  // Streifen, der von gestern erzaehlt, ist schlechter als keiner.
  onSuccess: () => absageWeg(),
})

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      // Eine fehlende oder abgelaufene Anmeldung lässt sich durch Wiederholen
      // nicht beheben -- dann wäre jeder Ladevorgang dreimal so langsam.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      // Ohne Netz **scheitern**, nicht anhalten.
      //
      // Von Haus aus hält TanStack Query jede Anfrage an, solange der Browser
      // offline meldet: das Versprechen wird dann nie eingelöst, weder mit
      // Erfolg noch mit Fehler. Das hat hier einen handfesten Schaden
      // angerichtet -- die Vorbereitung eines Fotos wartete auf die
      // Einstellungen, die Anfrage hing, und die Kachel stand für immer auf
      // „…". Das Offline-Verhalten steckt in dieser Anwendung im
      // Ausgangskorb; dafür muss ein Fehlschlag ein Fehlschlag sein.
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
})
// #126: an answer that left before an invalidation does not undo it.
dropAnswersFromBeforeInvalidation(queryClient)

const container = document.getElementById('root')
if (!container) throw new Error('Kein Wurzelelement gefunden.')

// **Vor dem ersten Zeichnen.** Danach gesetzt, sähe man den Chat einmal in
// der Vorgabegröße aufblitzen und dann umspringen.
schriftgroesseAnwenden()
// The margin under iOS 27's glass band (#48, 1.53.1) -- the same moment, for
// the same reason: a header that moves down after the first paint jumps.
glasbandAnwenden()

// **Der Service Worker wird jetzt von jedem Gerät angemeldet**, nicht mehr nur
// von denen mit Benachrichtigungen. Ohne ihn gibt es keine Hülle im
// Zwischenspeicher, und ohne die geht die App im Funkloch gar nicht erst auf.
// Ein Fehlschlag kostet genau das und sonst nichts -- deshalb geschluckt. Läuft
// nebenher, das Zeichnen wartet nicht darauf.
//
// **Geschluckt heißt nicht spurlos** (Codereview C10). Hier stand
// `.catch(() => undefined)`, und damit gab es für „die App geht im Funkloch
// nicht auf" nirgends eine Auskunft -- auch nicht unter *Einstellungen ›
// Diagnose*, wo man danach sucht. Der Grund geht in den Mitschrieb, die
// Anwendung läuft weiter.
void huelleAnmelden().catch((fehler) => notiere(`Hülle nicht angemeldet: ${String(fehler)}`))

// **Der Vorrat kommt vor dem ersten Zeichnen, und darauf wird gewartet.**
// Käme er später, hätte `RequireAuth` längst zur Anmeldung weitergeleitet --
// ohne Netz scheitert `useMe()` ja sofort. Der gerettete Stand träfe dann auf
// eine Seite, die es nicht mehr gibt.
//
// Angehängt und nicht mit `await` davor: ein `await` auf oberster Ebene lehnt
// das Zielumfeld ab (Safari 14 steht in der Liste), und ein Bündel, das dort
// nicht lädt, wäre ein hoher Preis für eine schönere Zeile.
//
// `vorratLaden` bringt seine eigene Zeitschranke mit und schluckt jeden
// Fehlschlag. Der `catch` hier ist die zweite Sperre: **nichts** darf das
// Zeichnen verhindern -- eine Anwendung, die wegen ihres Zwischenspeichers
// schwarz bleibt, wäre schlimmer als eine ohne.
void vorratLaden(queryClient)
  .catch(() => false)
  .then(() => {
    vorratMitschreiben(queryClient)

    createRoot(container).render(
      /*
        **Die Fehlergrenze steht ganz aussen und um alles herum.**

        Ohne sie haengt React bei einem Fehler im Zeichnen den ganzen Baum ab,
        und uebrig bleibt ein leeres Wurzelelement -- ein weisser Bildschirm,
        aus dem sich nichts ablesen laesst. Genau das kam aus dem Betrieb und
        kostete eine Runde Raten.

        Aussen und nicht innen: ein Fehler im `QueryClientProvider` oder im
        Router selbst soll auch noch etwas anzeigen.
      */
      <StrictMode>
        <Absturz>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </Absturz>
      </StrictMode>,
    )
  })
